/**
 * skylit-font-coverage.js — warn when the chosen typeface cannot draw the
 * caption the customer just typed.
 *
 * THE FAILURE THIS EXISTS TO CATCH
 *
 * A missing glyph is never an error. The browser silently borrows the letter
 * from the next font in the stack, so a Latvian caption in a brush script
 * comes out with upright system-sans letters wedged into the handwriting, and
 * the customizer shows it as if that were the design. Order SkylitStudio#197508
 * (2026-08-09) was approved by the customer in exactly that state, then printed.
 *
 * The bundled faces have since been rebuilt with the European accented letters
 * composed in (see skylit-admin/renderer/tools/patch_font_coverage.py), which
 * closes the case that actually happened. This is the guard for everything
 * else: Cyrillic, Greek, Hebrew, Arabic, CJK, emoji, and whatever face someone
 * adds to the grid next year without checking.
 *
 * HOW IT KNOWS
 *
 * Primarily by reading skylit-font-coverage.json, which is generated from the
 * cmaps of the very TTF files the print renderer loads
 * (skylit-admin/renderer/tools/build_theme_coverage.py). That makes the
 * warning exact and, more importantly, makes it agree with the print: it is
 * derived from the same bytes that end up on the paper. It is generated, not
 * hand-written, so it cannot drift the way a maintained list would — a
 * hardcoded table would already be wrong, since patching the faces changed
 * the answer for ten of them.
 *
 * For a face with no entry (a handwriting-mode font, or one added to the grid
 * before the data was regenerated) it falls back to measuring: render the
 * character in `Font, sentinel` and in `sentinel` alone, and treat identical
 * advance widths as a fallback. That heuristic is good enough to be worth
 * having and not good enough to rely on — it catches Latin gaps cleanly but
 * misses roughly a quarter of non-Latin characters, because putting a family
 * in front of a fallback can change which *system* font the browser reaches
 * for on a glyph that neither one has. Hence: data first, measurement second.
 *
 * WHAT IT WATCHES
 *
 * The rendered preview, not the input fields or any customizer's state. Every
 * customizer already paints the caption into the DOM with the chosen font
 * applied, so reading getComputedStyle off those elements works identically
 * for star maps, moon maps, family moons, location maps and custom prints,
 * with no per-section wiring to fall out of step.
 */
(function () {
  'use strict';

  // Captured at parse time: document.currentScript is null once we are inside
  // a callback, and the coverage JSON's URL can only come from Liquid
  // (asset_url adds the cache-busting ?v=), so the snippet hands it to us on
  // the script tag.
  var SCRIPT_EL = document.currentScript;

  /**
   * The caption text layer of each customizer — NOT the whole preview frame.
   *
   * The distinction is load-bearing. `.sm2-frame` looked like the obvious root
   * and is unusable: it contains #sm2-celestial-map, which is 5,500-odd nodes
   * of d3-celestial output plus the engine's own settings panel, so scanning
   * it meant a getComputedStyle call per star, and the panel's own labels
   * ("Horizon marker", "Projection"…) got scanned as if they were the
   * customer's caption. Worse, the celestial layer mutates continuously, which
   * starved the debounced rescan.
   *
   * These roots hold only the text the customer typed, and change only when
   * they type. Each was confirmed against the live customizer by typing a
   * marker into the caption field and finding where it surfaced.
   * An unknown selector is harmless — querySelectorAll just returns nothing.
   */
  var PREVIEW_ROOTS = [
    '#sm2-caption',               // star map: message + date/location lines
    '#lm-text-overlay',           // location map: title + subtitle
    '.custom-caption',            // moon map
    '#h-skyMap-title, #h-skyMap-subtitle',   // family moon
    '#preview-headline, #preview-caption',   // custom print
  ].join(',');

  /**
   * Three unlike fallbacks. The character is measured bare, with NO padding:
   * padding characters exist in every face, so they make the two stacks differ
   * on width no matter what, and every character then reads as covered. Bare
   * measurement can in principle collide — a face might happen to set one
   * character at exactly the fallback's advance — which is what the three
   * sentinels are for.
   */
  var SENTINELS = ['monospace', 'serif', 'cursive'];
  var cache = Object.create(null);
  var canvas = null;
  var ctx = null;

  function measure(text, stack) {
    if (!ctx) {
      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d');
    }
    ctx.font = '72px ' + stack;
    return ctx.measureText(text).width;
  }

  /**
   * Pull the face down before measuring it.
   *
   * Webfonts load lazily: a family the page has not yet painted has no bytes
   * in the browser, so measureText silently uses the fallback and EVERY
   * character reads as uncovered. Without this the warning fired on every
   * order, including plain English ones. Worse, Google splits families into
   * latin / latin-ext files, so a face can be "loaded" for the tile label and
   * still be missing the very subset we are asking about — passing the text
   * to fonts.load() is what makes it fetch the right one.
   */
  var loaded = Object.create(null);
  function ensureLoaded(family, text) {
    var key = family + '\u0000' + text;
    if (loaded[key]) return loaded[key];
    var p = (document.fonts && document.fonts.load)
      ? document.fonts.load('72px "' + family.replace(/"/g, '') + '"', text)
          .catch(function () { /* face may simply not exist; measure anyway */ })
      : Promise.resolve();
    loaded[key] = p;
    return p;
  }

  /** Generated cmap ranges, keyed by the picker's font name. */
  var COVERAGE = null;

  function inRanges(ranges, cp) {
    var lo = 0, hi = ranges.length - 1;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1;
      if (cp < ranges[mid][0]) hi = mid - 1;
      else if (cp > ranges[mid][1]) lo = mid + 1;
      else return true;
    }
    return false;
  }

  /**
   * True when `family` has a real glyph for `ch`.
   *
   * Exact when the generated data has the face. Otherwise measured, which
   * requires ensureLoaded() to have resolved for that family first.
   */
  function covers(family, ch) {
    if (COVERAGE && COVERAGE[family]) {
      return inRanges(COVERAGE[family], ch.codePointAt(0));
    }
    var key = family + '\u0000' + ch;
    if (key in cache) return cache[key];
    var quoted = '"' + family.replace(/"/g, '') + '"';
    var different = true;
    for (var i = 0; i < SENTINELS.length; i++) {
      // Matching ANY sentinel means the glyph came from that fallback rather
      // than from the family. Requiring all three to match was measured over
      // 18 faces and 30 characters: same zero false positives, twice the
      // misses. So one match is enough to call it missing.
      if (measure(ch, quoted + ',' + SENTINELS[i]) === measure(ch, SENTINELS[i])) {
        different = false;
        break;
      }
    }
    cache[key] = different;
    return different;
  }

  /**
   * Emoji are not this warning's business. No face in the grid has ever
   * contained them and none is expected to: the colour glyph comes from the
   * system emoji font in the preview and, identically, on the render machine
   * (see renderer/tools/fetch_fluent_emoji.py for the same reasoning applied
   * to location-map markers). Warning that Caveat "does not include" 💖 is
   * telling the customer their heart is broken when it prints exactly as
   * shown. Extended_Pictographic is the right cut: it catches 💖 and ❤ while
   * still leaving ★ and ✓ to be warned about, since those genuinely do fall
   * back to a mismatched system face.
   */
  var EMOJI_RE;
  try {
    EMOJI_RE = new RegExp('[\\u200d\\ufe0e\\ufe0f\\u20e3]|\\p{Extended_Pictographic}|\\p{Emoji_Modifier}|\\p{Regional_Indicator}', 'u');
  } catch (e) {
    // No Unicode property escapes. Block ranges, coarser but never throwing.
    EMOJI_RE = /[\u200d\ufe0e\ufe0f\u20e3\u2600-\u27bf\u2b00-\u2bff]|[\ud83c-\ud83e][\udc00-\udfff]/;
  }

  /**
   * The characters of `text` worth checking: non-ASCII, non-space, non-emoji,
   * deduped.
   *
   * Iterates by CODE POINT, not by code unit. `text[i]` splits every astral
   * character into two lone surrogates, and a lone surrogate is missing from
   * every font by definition — so an emoji used to produce two phantom gaps
   * that rendered as blanks in the warning ("does not include  and .").
   */
  function interesting(text) {
    var out = [];
    var chars = Array.from ? Array.from(text) : text.split(/(?:)/u);
    for (var i = 0; i < chars.length; i++) {
      var ch = chars[i];
      // ASCII is covered by every face in the grid, and re-checking it on
      // each keystroke is nearly all of the work for none of the value.
      if (ch.codePointAt(0) < 0xa1 || /\s/.test(ch)) continue;
      if (EMOJI_RE.test(ch)) continue;
      if (out.indexOf(ch) === -1) out.push(ch);
    }
    return out;
  }

  /**
   * Characters of `text` that `family` cannot draw, deduped, in order.
   * Async because the face has to be fetched before it can be measured.
   */
  function missing(text, family) {
    var chars = interesting(text);
    if (!chars.length) return Promise.resolve([]);
    return ensureLoaded(family, chars.join('')).then(function () {
      return chars.filter(function (ch) { return !covers(family, ch); });
    });
  }

  /**
   * Is this caption written in a script other than Latin?
   *
   * The trigger for hiding faces from the picker, and deliberately narrower
   * than "contains a character some face lacks". Filtering removes most of
   * the customer's choices, which is proportionate when the whole caption is
   * Cyrillic and absurd when they typed one star: the ornament case is what
   * the warning text is for.
   *
   * Latin Extended Additional (U+1E00-1EFF) is pointedly NOT here. That is
   * where Vietnamese lives, the faces were patched to cover it, and treating
   * it as foreign would hide the grid from a market we actually serve. Greek
   * Extended sits immediately after it at U+1F00, so the boundary between
   * "supported Latin" and "another script" falls between two adjacent blocks
   * — hence the explicit table rather than a coarse "above U+0370" test.
   */
  /* @lm-test-export:start hasNonLatinScript */
  function hasNonLatinScript(text) {
    var RANGES = [
      [0x0370, 0x03ff],  // Greek and Coptic
      [0x0400, 0x052f],  // Cyrillic + Supplement
      [0x0530, 0x058f],  // Armenian
      [0x0590, 0x05ff],  // Hebrew
      [0x0600, 0x06ff],  // Arabic
      [0x0700, 0x074f],  // Syriac
      [0x0750, 0x077f],  // Arabic Supplement
      [0x0780, 0x07bf],  // Thaana
      [0x0800, 0x08ff],  // Samaritan .. Arabic Extended-A
      [0x0900, 0x0dff],  // Devanagari .. Sinhala
      [0x0e00, 0x0fff],  // Thai, Lao, Tibetan
      [0x1000, 0x109f],  // Myanmar
      [0x10a0, 0x10ff],  // Georgian
      [0x1100, 0x11ff],  // Hangul Jamo
      [0x1200, 0x137f],  // Ethiopic
      [0x13a0, 0x13ff],  // Cherokee
      [0x1400, 0x167f],  // Unified Canadian Aboriginal Syllabics
      [0x1700, 0x17ff],  // Tagalog .. Khmer
      [0x1800, 0x18af],  // Mongolian
      [0x1f00, 0x1fff],  // Greek Extended  (NOT 1E00-1EFF, see above)
      [0x2de0, 0x2dff],  // Cyrillic Extended-A
      [0x2e80, 0x2fdf],  // CJK radicals
      [0x3000, 0x303f],  // CJK symbols and punctuation
      [0x3040, 0x30ff],  // Hiragana + Katakana
      [0x3100, 0x312f],  // Bopomofo
      [0x3130, 0x318f],  // Hangul Compatibility Jamo
      [0x31f0, 0x31ff],  // Katakana Phonetic Extensions
      [0x3400, 0x4dbf],  // CJK Unified Ideographs Extension A
      [0x4e00, 0x9fff],  // CJK Unified Ideographs
      [0xa000, 0xa4cf],  // Yi
      [0xa640, 0xa69f],  // Cyrillic Extended-B
      [0xa960, 0xa97f],  // Hangul Jamo Extended-A
      [0xac00, 0xd7af],  // Hangul Syllables
      [0xf900, 0xfaff],  // CJK Compatibility Ideographs
      [0xfb1d, 0xfb4f],  // Hebrew presentation forms
      [0xfb50, 0xfdff],  // Arabic Presentation Forms-A
      [0xfe70, 0xfeff],  // Arabic Presentation Forms-B
      [0x20000, 0x2fa1f] // CJK Unified Ideographs Extensions B-F
    ];
    // By code point, not code unit: an astral CJK character is two units and
    // neither half falls in any range above.
    var chars = Array.from ? Array.from(text) : text.split(/(?:)/u);
    for (var i = 0; i < chars.length; i++) {
      var cp = chars[i].codePointAt(0);
      for (var r = 0; r < RANGES.length; r++) {
        if (cp >= RANGES[r][0] && cp <= RANGES[r][1]) return true;
      }
    }
    return false;
  }
  /* @lm-test-export:end hasNonLatinScript */

  /**
   * Which face to move the caption to when the active one cannot draw it.
   *
   * Category first. A customer who picked Alex Brush wants handwriting, and
   * dropping them onto Prata because it happens to be the first covering tile
   * in the grid would swap their script for a serif — a bigger change than
   * the one they asked for. Only when nothing in their category covers the
   * text do we cross over, and if nothing covers it at all we return null and
   * leave the selection alone, because moving them to an equally broken face
   * helps nobody and the warning already explains the situation.
   *
   * `faces` is in DOM order, so "first covering" means the leftmost tile —
   * stable, and the same answer every time for the same grid.
   */
  /* @lm-test-export:start pickReplacementFont */
  function pickReplacementFont(activeName, faces, coveringNames) {
    var covers = Object.create(null);
    for (var i = 0; i < coveringNames.length; i++) covers[coveringNames[i]] = true;
    if (covers[activeName]) return activeName;

    var activeCategory = null;
    for (var j = 0; j < faces.length; j++) {
      if (faces[j].name === activeName) { activeCategory = faces[j].category; break; }
    }
    var k;
    if (activeCategory) {
      for (k = 0; k < faces.length; k++) {
        if (faces[k].category === activeCategory && covers[faces[k].name]) return faces[k].name;
      }
    }
    for (k = 0; k < faces.length; k++) {
      if (covers[faces[k].name]) return faces[k].name;
    }
    return null;
  }
  /* @lm-test-export:end pickReplacementFont */

  /** The face names the picker offers, read from the tiles themselves. */
  function offeredFonts() {
    var tiles = document.querySelectorAll('.sm2-font-btn[data-font], .lm-font-btn[data-font]');
    var names = [];
    for (var i = 0; i < tiles.length; i++) {
      var n = tiles[i].getAttribute('data-font');
      if (n && names.indexOf(n) === -1) names.push(n);
    }
    return names;
  }

  /** First family in a computed font-family list, unquoted. */
  function primaryFamily(el) {
    var stack = getComputedStyle(el).fontFamily || '';
    return stack.split(',')[0].trim().replace(/^["']|["']$/g, '');
  }

  function list(items) {
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }

  function ensureNotice() {
    var existing = document.querySelector('.skylit-font-warning');
    if (existing) return existing;
    var grid = document.querySelector('.sm2-font-grid, .lm-font-grid');
    if (!grid || !grid.parentNode) return null;
    var el = document.createElement('p');
    el.className = 'skylit-font-warning';
    el.setAttribute('role', 'status');
    el.hidden = true;
    grid.parentNode.insertBefore(el, grid.nextSibling);
    return el;
  }

  /** The caption text currently painted in each of the picker's faces. */
  function previewTextByFont(offered) {
    var byFont = Object.create(null);
    var roots = document.querySelectorAll(PREVIEW_ROOTS);
    for (var r = 0; r < roots.length; r++) {
      var nodes = roots[r].querySelectorAll('*');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        var text = '';
        for (var c = 0; c < el.childNodes.length; c++) {
          if (el.childNodes[c].nodeType === 3) text += el.childNodes[c].nodeValue;
        }
        if (!text.trim()) continue;
        var family = primaryFamily(el);
        if (offered.indexOf(family) === -1) continue;
        byFont[family] = (byFont[family] || '') + text;
      }
    }
    return byFont;
  }

  /**
   * Scans overlap: each one awaits font fetches, so a scan started earlier can
   * finish later and repaint the notice with a caption the customer has since
   * replaced. Observed as a stale Cyrillic warning surviving after the text was
   * changed to Latvian. Every scan takes a ticket and only the newest writes.
   */
  var generation = 0;

  /**
   * Attach to any caption root not already watched. Called again on each scan
   * because some roots are created late — custom print's #preview-headline
   * only exists once an image has been uploaded, so watching only what was on
   * the page at boot would leave that customizer unguarded.
   */
  var watched = [];
  function observeRoots() {
    var roots = document.querySelectorAll(PREVIEW_ROOTS);
    for (var i = 0; i < roots.length; i++) {
      if (watched.indexOf(roots[i]) !== -1) continue;
      watched.push(roots[i]);
      new MutationObserver(schedule).observe(roots[i], {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['style', 'class', 'data-font'],
      });
    }
  }

  /**
   * The caption split by ROLE, which PREVIEW_ROOTS deliberately does not do.
   *
   * The picker applies a font to the message, the details, or both, so the
   * filter has to know which text each choice would actually be set in. They
   * diverge more often than they look like they would: the details line
   * carries the LOCATION NAME, so "Москва, Россия" needs Cyrillic in the
   * details while the message is still plain English.
   *
   * Same rule as PREVIEW_ROOTS — an unknown selector matches nothing and
   * costs nothing, so every customizer's hooks can live in one table.
   */
  var ROLE_ROOTS = {
    message: [
      '.sm2-caption-text-block',   // star map
      '.lm-preview-title',         // location map
      '.custom-textarea',          // moon map
      '#h-skyMap-title',           // family moon
      '#preview-headline',         // custom print
    ].join(','),
    details: [
      '.sm2-caption-details',      // star map
      '.lm-preview-subtitle, .lm-preview-coords',
      '.custom-address',           // moon map
      '#h-skyMap-subtitle',        // family moon
      '#preview-caption',          // custom print
    ].join(','),
  };

  function roleText(role) {
    var nodes = document.querySelectorAll(ROLE_ROOTS[role]);
    var out = '';
    for (var i = 0; i < nodes.length; i++) out += ' ' + (nodes[i].textContent || '');
    return out;
  }

  /**
   * Which scope the picker is on: message | details | both.
   *
   * The star map and the two moons carry data-scope on the tab; the location
   * map's tabs were written with Alpine @click handlers and no attribute, so
   * fall back to the visible label. Reading the DOM rather than each
   * customizer's store keeps this module free of four different state shapes.
   */
  function activeScope() {
    // [data-scope] is load-bearing, not decoration: the star map reuses
    // .sm2-font-scope-btn for a SECOND, unrelated pair of tabs (Typed /
    // Handwritten) that carry no data-scope. Matching the bare class picked
    // up "Typed", read no scope off it, and silently fell back to message —
    // so the Details tab could never have been detected.
    var btn = document.querySelector(
      '.sm2-font-scope-btn.active[data-scope], .lm-font-scope-btn.active');
    if (!btn) return 'message';
    var s = btn.getAttribute('data-scope') ||
            (btn.textContent || '').trim().toLowerCase();
    return (s === 'details' || s === 'both') ? s : 'message';
  }

  /** The text a font chosen right now would be applied to. */
  function scopedText(scope) {
    if (scope === 'details') return roleText('details');
    if (scope === 'both') return roleText('message') + ' ' + roleText('details');
    return roleText('message');
  }

  /**
   * The picker's tiles as [{name, category, el}], in DOM order.
   *
   * Category comes from the `.sm2-font-category` / `.lm-font-category`
   * headings the grid already renders ("Regular", "Handwritten") rather than
   * a table duplicated here, so a face added to the markup is categorised
   * correctly without touching this file.
   */
  function gridFaces() {
    var grids = document.querySelectorAll('.sm2-font-grid, .lm-font-grid');
    var faces = [];
    for (var g = 0; g < grids.length; g++) {
      var category = '';
      var kids = grids[g].children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el.classList.contains('sm2-font-category') ||
            el.classList.contains('lm-font-category')) {
          category = (el.textContent || '').trim().toLowerCase();
          continue;
        }
        var name = el.getAttribute && el.getAttribute('data-font');
        if (name) faces.push({ name: name, category: category, el: el });
      }
    }
    return faces;
  }

  function activeFontName() {
    var el = document.querySelector(
      '.sm2-font-btn.active[data-font], .lm-font-btn.active[data-font]');
    return el ? el.getAttribute('data-font') : null;
  }

  function showAllFaces(faces) {
    for (var i = 0; i < faces.length; i++) {
      faces[i].el.classList.remove('skylit-font-unavailable');
    }
  }

  /**
   * Hide the faces that cannot set this caption, and move the customer off
   * one if they are standing on it.
   *
   * Only for captions in another script. A face missing one ornament stays in
   * the grid — see hasNonLatinScript. Returns the notice text to display, or
   * null to leave the normal per-glyph warning in charge.
   */
  // The switch we most recently made, so its explanation survives the rescan
  // the switch itself triggers. Without this the message paints and is wiped
  // a beat later by the per-glyph pass, which finds nothing wrong precisely
  // BECAUSE the switch worked — the customer sees a flash and no reason.
  var switchedFrom = null;
  var switchedTo = null;

  function switchMessageFor(active) {
    if (!switchedFrom || switchedTo !== active) return null;
    return switchedFrom + ' cannot print this text, so we have switched you ' +
           'to ' + switchedTo + '. Fonts that cannot print it are hidden ' +
           'until your text changes.';
  }

  function filterGrid(offered) {
    var faces = gridFaces();
    if (!faces.length) return Promise.resolve(null);

    var scope = activeScope();
    var text = scopedText(scope);
    if (!hasNonLatinScript(text)) {
      showAllFaces(faces);
      // Back to Latin: the grid returns, and the customer STAYS on the face
      // we moved them to. Restoring their old one would be a second
      // unrequested change, and by now they have seen this one for a while.
      switchedFrom = switchedTo = null;
      return Promise.resolve(null);
    }

    // Every face has to be pulled down before it can be measured, exactly as
    // the warning path does — an unfetched face reads as covering nothing and
    // would empty the grid.
    return Promise.all(offered.map(function (f) {
      return ensureLoaded(f, text).then(function () {
        return { name: f, ok: !interesting(text).some(function (ch) {
          return !covers(f, ch);
        }) };
      });
    })).then(function (results) {
      var covering = results.filter(function (r) { return r.ok; })
                            .map(function (r) { return r.name; });
      // Nothing covers it — hiding the whole grid would leave the customer
      // with no choices at all, which is worse than an honest warning.
      if (!covering.length) {
        showAllFaces(faces);
        return null;
      }

      for (var i = 0; i < faces.length; i++) {
        var supported = covering.indexOf(faces[i].name) !== -1;
        faces[i].el.classList.toggle('skylit-font-unavailable', !supported);
      }

      var active = activeFontName();
      if (!active) return null;
      if (covering.indexOf(active) !== -1) return switchMessageFor(active);

      var replacement = pickReplacementFont(active, faces, covering);
      if (!replacement) return null;
      for (var j = 0; j < faces.length; j++) {
        if (faces[j].name === replacement) {
          // Click the real tile rather than reaching into any customizer's
          // state: every one of them already routes selection through this
          // handler, so state, Alpine and autosave all stay consistent and
          // this module stays ignorant of four different store shapes.
          faces[j].el.click();
          break;
        }
      }
      switchedFrom = active;
      switchedTo = replacement;
      return switchMessageFor(replacement);
    });
  }

  function scan() {
    observeRoots();
    var mine = ++generation;
    var current = function () { return mine === generation; };
    var offered = offeredFonts();
    if (!offered.length) return Promise.resolve();
    var notice = ensureNotice();
    if (!notice) return Promise.resolve();

    return filterGrid(offered).then(function (switchMessage) {
      if (!current()) return;
      if (switchMessage) {
        // The switch is the more important thing to say, and saying both at
        // once would contradict: the face we just moved them to covers the
        // text, so the per-glyph warning has nothing left to report anyway.
        notice.textContent = switchMessage;
        notice.hidden = false;
        return;
      }
      return scanGaps(offered, notice, current);
    });
  }

  /** The per-glyph warning: which characters the chosen face cannot draw. */
  function scanGaps(offered, notice, current) {
    var byFont = previewTextByFont(offered);
    var families = Object.keys(byFont);
    if (!families.length) {
      notice.hidden = true;
      return Promise.resolve();
    }

    return Promise.all(families.map(function (family) {
      return missing(byFont[family], family).then(function (gaps) {
        return { family: family, gaps: gaps };
      });
    })).then(function (results) {
      if (!current()) return;
      var hit = results.filter(function (r) { return r.gaps.length; })[0];
      if (!hit) {
        notice.hidden = true;
        return;
      }
      // Offer the faces that can draw what this one cannot. They have to be
      // fetched before they can be measured, same as the failing face.
      var others = offered.filter(function (f) { return f !== hit.family; });
      var probe = hit.gaps.join('');
      return Promise.all(others.map(function (f) {
        return ensureLoaded(f, probe).then(function () { return f; });
      })).then(function (ready) {
        if (!current()) return;
        var alternatives = ready.filter(function (f) {
          return hit.gaps.every(function (ch) { return covers(f, ch); });
        }).slice(0, 2);
        notice.textContent =
          hit.family + ' does not include ' + list(hit.gaps.slice(0, 8)) +
          '. Those letters will print in a different style.' +
          (alternatives.length
            ? ' ' + list(alternatives) +
              (alternatives.length > 1 ? ' do' : ' does') + ' include them.'
            : '');
        notice.hidden = false;
      });
    });
  }

  /**
   * Debounce, but with a ceiling. A pure debounce is wrong here: a preview
   * that mutates continuously keeps pushing the timer back and the scan never
   * runs at all, which showed up as the notice lagging a caption behind and
   * then sticking. The first pending scan is guaranteed to fire within
   * MAX_WAIT no matter how much churn follows it.
   */
  var DEBOUNCE = 250;
  var MAX_WAIT = 1000;
  var pending = null;
  var firstRequest = 0;
  function schedule() {
    var now = Date.now();
    if (!pending) firstRequest = now;
    else clearTimeout(pending);
    var wait = Math.min(DEBOUNCE, Math.max(0, firstRequest + MAX_WAIT - now));
    pending = setTimeout(function () { pending = null; scan(); }, wait);
  }

  function start() {
    // Two prerequisites, both of which produce a wrong answer if skipped:
    // the generated coverage data (exact, and what the print will do), and
    // the webfonts themselves (a face with no bytes measures as uncovered).
    var url = SCRIPT_EL && SCRIPT_EL.getAttribute('data-coverage-url');
    var data = url
      ? fetch(url).then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) { COVERAGE = j; })
          .catch(function () { COVERAGE = null; })
      : Promise.resolve();
    var fontsReady = document.fonts && document.fonts.ready
      ? document.fonts.ready
      : Promise.resolve();
    Promise.all([data, fontsReady]).then(function () {
      cache = Object.create(null);
      scan();
      observeRoots();
      document.addEventListener('input', schedule, true);
      // Changing the SCOPE changes which text the grid must be filtered
      // against, but it does not touch the preview — so the MutationObserver
      // never fires and, without this, the grid keeps the previous scope's
      // filter. That showed up as an English message still offering only the
      // five Cyrillic-capable faces because Details had been selected before.
      document.addEventListener('click', function (e) {
        if (e.target.closest(
          '.sm2-font-scope-btn, .lm-font-scope-btn, .sm2-font-btn, .lm-font-btn')) {
          schedule();
        }
      }, true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.SkylitFontCoverage = {
    missing: missing,
    covers: covers,
    scan: scan,
    /** Faces answered from generated data rather than measured. Debug aid. */
    exactFaces: function () { return COVERAGE ? Object.keys(COVERAGE) : []; },
  };
})();
